/* eslint-disable unused-imports/no-unused-imports */
import {connect} from "react-redux"
import {createSelector} from "reselect"
import {breakpoints} from "@next/themes"
import {CMS_PAGE_ID, SearchApiRequestTypes} from "../../config/constants"
import State from "../../models/State"
import {AdsConfig, AdConfig, Ad} from "../../models/SearchState"
import {IS_BROWSER} from "../../utils/window"

import {
    fixFacets,
    unfixFacets,
    selectHasNextPage,
    selectHasPreviousPage,
    fetchNextPageItemsThunk,
    fetchPreviousPageItemsThunk,
    setInjectedAdsHiddenForTimeoutAction,
    setInitialScrollCompleted,
    setFirstElementId,
    setNewPageItemsAction,
} from "../../ducks/search"
import {pushSearchResultsEvent} from "../../utils/pushSearchResultsEvent"
import {selectFetchTriggerOffset} from "../../ducks/search/utils"
import {ProductItem} from "../../models/Product"

import breakpointToViewportSize from "../../utils/breakpointToViewportSize"

export function getAdsPosition(config: AdsConfig | AdConfig, injectAd: boolean) {
    // Return the position of the Google Ads
    if (injectAd) {
        return (config as AdConfig).adDetails?.position
    }

    // Return the position of the CMS Ads based on the window width
    if (IS_BROWSER() && config?.enabled) {
        const windowWidth = window.outerWidth
        const {xs, sm, md, lg, xl, xxl} = breakpoints.values
        const adsPositions = [] as {position: number; tileCount?: number}[]
        ;(config as AdsConfig).ads.forEach((ad: Ad) => {
            const {positions} = ad
            const breakpointsMap = [
                {max: xs, position: positions.xs},
                {max: sm, position: positions.sm},
                {max: md, position: positions.md},
                {max: lg, position: positions.lg},
                {max: xl, position: positions.xl},
                {max: xxl - 1, position: positions.xl},
                {max: Infinity, position: positions.xxl},
            ]
            const pos: number | undefined = breakpointsMap.find(bp => windowWidth <= bp.max)?.position
            adsPositions.push({position: pos as number, tileCount: ad.tileCount})
        })
        return adsPositions
    }
}

const selectItems = (state: State) => state.search.items
const selectStartPage = (state: State) => state.search.startPage
const selectInjectedAdsConfig = (state: State) => state.search.injectedAdsConfig
const selectInjectAd = (state: State, injectAd: boolean) => injectAd // for Google Ads
const selectAdsConfig = (state: State) => state.search.adsConfig // for CMS Ads
const selectRequestedPage = (state: State) => state.request.page
const selectAdvertsContent = (state: State) => state.search.advertsContent
const selectUrl = (state: State) => state.request.url

export const makeSelectItems = createSelector(
    [
        selectItems,
        selectStartPage,
        selectInjectedAdsConfig,
        selectInjectAd,
        selectAdsConfig,
        selectRequestedPage,
        selectAdvertsContent,
        selectUrl,
    ],
    (items, startPage, injectedAdsConfig, injectAd, adsConfig, requestedPage, advertsContent, url) => {
        const fragmentID = url.includes(CMS_PAGE_ID) ? url.split(`${CMS_PAGE_ID}=`)[1]?.split("&")[0] : null

        const adSpacingItem = {
            itemNumber: "ad_placeholder_item",
            newIn: false,
            type: "product",
            title: "bundle item",
        }

        /*  
            @injectedAdsConfig : This is for Google Ads configuration
            @adsConfig : This is for CMS Ads configuration
            @injectAd : This is for Google Ads to check it is enable/disable
        */
        const itemsWithSpacing: ProductItem[] = []
        const adConfig = injectAd ? injectedAdsConfig : adsConfig
        // If the startPage is 1 and requestedPage is less than or equal to 1, we reserve space for adverts
        // and inject the ad spacing item at the specified position
        // This is to ensure that adverts are shown only on the first page of the PLP
        if (
            startPage === 1 &&
            requestedPage <= 1 &&
            (adsConfig?.enabled ? advertsContent?.length || fragmentID : injectAd)
        ) {
            const ad = getAdsPosition(adConfig as AdsConfig | AdConfig, injectAd)
            items.forEach((item, index) => {
                // adsPosition can be a single number in case of Google Ads or an array of numbers in case of CMS Ads
                if (index === ad) {
                    itemsWithSpacing.push({...adSpacingItem, adsId: index})
                } else if (Array.isArray(ad) && ad.some(a => a.position === index)) {
                    const tileCount = ad.find(a => a.position === index)?.tileCount
                    itemsWithSpacing.push({...adSpacingItem, adsId: index, tileCount})
                }
                itemsWithSpacing.push(item)
            })
        }
        return startPage === 1 &&
            requestedPage <= 1 &&
            (adsConfig?.enabled ? advertsContent?.length || fragmentID : injectAd)
            ? itemsWithSpacing
            : items
    },
)

// Selector for facets
const selectFacets = (state: State) => state.search.facets

// Memoized selector for filterSelectedCount
const makeSelectFilterSelectedCount = createSelector([selectFacets], facets =>
    Object.values(facets).reduce((acc, facet) => {
        if (facet.s) {
            return acc + 1
        }
        return acc
    }, 0),
)

const makeHasSelectedFilters = createSelector([selectFacets], facets => Object.values(facets).some(f => f.s))

const mapStateToProps = (state: State) => {
    const {
        search: {
            injectedAdsConfig,
            injectedAdHiddenForTimeout,
            sorting,
            consentCookieValue,
            hideFooterUntilLastPage,
            productLoadTrigger,
            templateOverride: {productSummaryTemplateName = undefined, imageRatio = undefined} = {},
            currentBreakpoint,
            itemsPerPage: {initial, subsequent},
            subsidiseItemsForLoadMore,
            adsConfig,
            advertsContent,
        },
        request: {type, im, urlIsSaleOrClearance, saleOrClearance, page},
        features: {enableNewProductSummary},
    } = state

    let consentedToAds = false
    let injectAd = false

    if (injectedAdsConfig.enabled) {
        // has user consented to ads?
        const consentValue = consentCookieValue.groups.find(x => x.name === "Targt")

        if (consentValue && consentCookieValue.consentBoxHasClosed) {
            consentedToAds = consentValue.value as boolean
        }

        // is ad enabled for specific search request type?
        const adsEnabledForSearchType: boolean =
            type === SearchApiRequestTypes.Category
                ? injectedAdsConfig.categorySearchAds
                : injectedAdsConfig.keywordSearchAds

        injectAd = adsEnabledForSearchType && consentedToAds && !injectedAdHiddenForTimeout
    }

    return {
        items: makeSelectItems(state, injectAd),
        endPage: state.search.endPage,
        startPage: state.search.startPage,
        itemsPerPage: subsidiseItemsForLoadMore
            ? initial[breakpointToViewportSize(currentBreakpoint)]
            : subsequent[breakpointToViewportSize(currentBreakpoint)],
        itemsPerRow: state.search.itemsPerRow,
        hasNextPage: selectHasNextPage(state),
        requestedPage: page,
        hasPreviousPage: selectHasPreviousPage(state),
        isFetchingNextPage: state.search.isFetchingNextPage,
        isFetchingPreviousPage: state.search.isFetchingPreviousPage,
        isFetchingPageItems: state.search.isFetchingPageItems,
        siteUrl: state.request.siteUrl,
        useDevEsi: state.request.useDevEsi,
        fetchTriggerOffset: selectFetchTriggerOffset(state),
        isAutocorrected: Boolean(state.search.autoCorrectQuery),
        hasSelectedFilters: makeHasSelectedFilters(state),
        filterSelectedCount: makeSelectFilterSelectedCount(state),
        text: state.text,
        url: state.request.url,
        searchCategoryId: state.search.searchCategory.id,
        searchCategoryName: state.search.searchCategory.name,
        initialItemsPerPage: state.search.initialItemsPerPage,
        initialScrollCompleted: state.search.initialScrollCompleted,
        firstElementId: state.search.firstElementId,
        injectedAdHiddenForTimeout,
        injectedAdsEnabled: injectedAdsConfig.enabled, // This is for Google Ads
        isAdsEnabled: adsConfig.enabled, // This is for CMS Ads
        selectedSort: sorting.selected,
        consentedToAds,
        hideFooterUntilLastPage,
        productLoadTrigger,
        im,
        productSummaryTemplateName,
        imageRatio,
        enableNewProductSummary,
        urlIsSaleOrClearance,
        saleOrClearance,
        advertsContent,
        adsConfig,
    }
}

export const mapDispatchToProps = {
    fixFacets,
    unfixFacets,
    fetchNextPage: fetchNextPageItemsThunk,
    fetchPreviousPage: fetchPreviousPageItemsThunk,
    pushSearchResultsEvent: () => (_dispatch, getState) => {
        pushSearchResultsEvent(getState())
    },
    removeAdSpacerFromItems: () => (dispatch, getState) => {
        const state = getState()
        const itemsFiltered = state.search.items.filter(item => item.itemNumber !== "ad_placeholder_item")
        dispatch(setNewPageItemsAction(itemsFiltered))
        dispatch(setInjectedAdsHiddenForTimeoutAction(true))
    },
    setInitialScrollCompleted,
    setFirstElementId,
}

export default connect(mapStateToProps, mapDispatchToProps)
